import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiUploadCloud, FiEye, FiDownload, FiTrash2, FiFile, FiImage, FiFileText } from 'react-icons/fi';
import api from '../../services/api';

// The three the office actually chases a vendor for, plus room for the rest.
const DOC_TYPES = ['W-9', 'Insurance', 'Bank Info', 'Contract', 'License', 'Other'];

const TradePartnerDocsTab = ({ partnerId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [docType, setDocType] = useState('W-9');
  const fileInputRef = useRef(null);

  const loadDocs = useCallback(async () => {
    if (!partnerId) return;
    try {
      const res = await api.get(`/documents/trade-partner/${partnerId}`);
      setDocuments(res.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }
    // Whatever the selector reads when the drop happens is what the files are
    // filed as — the operator picks the type first, then drops.
    formData.append('doc_type', docType);

    try {
      const res = await api.post(`/documents/trade-partner/${partnerId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDocuments(prev => [...res.data, ...prev]);
      toast.success(`${files.length} file(s) uploaded`);
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  const handleFileSelect = (e) => {
    uploadFiles(e.target.files);
    e.target.value = '';
  };

  const handleTypeChange = async (doc, value) => {
    const previous = doc.doc_type;
    setDocuments(prev => prev.map(d => (d.id === doc.id ? { ...d, doc_type: value } : d)));
    try {
      await api.put(`/documents/trade-partner/doc/${doc.id}`, { doc_type: value });
    } catch (error) {
      setDocuments(prev => prev.map(d => (d.id === doc.id ? { ...d, doc_type: previous } : d)));
      toast.error('Failed to change type');
    }
  };

  const handlePreview = (doc) => {
    const token = localStorage.getItem('token');
    window.open(`/api/documents/trade-partner/preview/${doc.id}?token=${token}`, '_blank');
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/documents/trade-partner/download/${doc.id}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.original_name}"?`)) return;
    try {
      await api.delete(`/documents/trade-partner/doc/${doc.id}`);
      setDocuments(prev => prev.filter(d => d.id !== doc.id));
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${month}-${day}-${d.getFullYear()} ${hours}:${mins}`;
  };

  const getFileIcon = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return <FiImage size={15} className="file-icon file-icon-image" />;
    }
    if (ext === 'pdf') {
      return <FiFileText size={15} className="file-icon file-icon-pdf" />;
    }
    return <FiFile size={15} className="file-icon file-icon-default" />;
  };

  if (!partnerId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
        <p>Please save the trade partner first.</p>
      </div>
    );
  }

  const onFile = documents.reduce((acc, d) => {
    acc[d.doc_type] = (acc[d.doc_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="doc-center-tab">
      <div className="doc-center-layout">
        <div className="doc-upload-panel">
          <div className="doc-upload-panel-header">SELECT FILES</div>

          <div style={{ padding: '10px 12px 0' }}>
            <label style={{ fontSize: '11px', fontWeight: 600, color: '#64748b' }}>
              DOCUMENT TYPE
            </label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              style={{
                width: '100%', marginTop: '4px', padding: '6px 8px', fontSize: '13px',
                border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff',
              }}
            >
              {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div
            className={`doc-drop-zone ${dragOver ? 'drag-over' : ''}`}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
            onClick={() => fileInputRef.current?.click()}
          >
            <FiUploadCloud size={32} className="drop-zone-icon" />
            <p className="drop-zone-text">Drag and drop file(s) here...</p>
          </div>

          <div className="doc-upload-btn-row">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              style={{ display: 'none' }}
            />
            <button
              className="btn-choose-files"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Choose Files'}
            </button>
          </div>

          <div style={{ padding: '12px', fontSize: '12px', color: '#64748b' }}>
            {['W-9', 'Insurance', 'Bank Info'].map(t => (
              <div key={t} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                <span>{t}</span>
                <span style={{ color: onFile[t] ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                  {onFile[t] ? `${onFile[t]} on file` : 'missing'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="doc-list-panel">
          <div className="doc-list-header">
            <span className="doc-list-title">DOCUMENT LIST</span>
            <span className="doc-list-count">{documents.length} file(s)</span>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
          ) : documents.length === 0 ? (
            <div className="doc-empty">
              <p>No documents uploaded yet.</p>
              <p>Pick a type, then drag files in or click "Choose Files".</p>
            </div>
          ) : (
            <div className="doc-table-wrapper">
              <table className="doc-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>NAME</th>
                    <th>TYPE</th>
                    <th>DATE</th>
                    <th>SIZE</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="doc-icon-cell">{getFileIcon(doc.original_name)}</td>
                      <td className="doc-name-cell">
                        <span className="doc-name" title={doc.original_name}>
                          {doc.original_name}
                        </span>
                      </td>
                      <td>
                        <select
                          value={doc.doc_type || 'Other'}
                          onChange={(e) => handleTypeChange(doc, e.target.value)}
                          style={{
                            fontSize: '12px', padding: '2px 4px', border: '1px solid #e2e8f0',
                            borderRadius: '4px', background: '#fff', color: '#334155',
                          }}
                        >
                          {[...new Set([...DOC_TYPES, doc.doc_type || 'Other'])].map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </td>
                      <td className="doc-date-cell">{formatDate(doc.created_at)}</td>
                      <td className="doc-size-cell">{formatFileSize(doc.file_size)}</td>
                      <td className="doc-actions-cell">
                        <button
                          className="doc-action-btn doc-action-preview"
                          onClick={() => handlePreview(doc)}
                          title="Preview"
                        >
                          <FiEye size={14} />
                        </button>
                        <button
                          className="doc-action-btn doc-action-download"
                          onClick={() => handleDownload(doc)}
                          title="Download"
                        >
                          <FiDownload size={14} />
                        </button>
                        <button
                          className="doc-action-btn doc-action-delete"
                          onClick={() => handleDelete(doc)}
                          title="Delete"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradePartnerDocsTab;
